import styled from '@emotion/styled';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { AppState } from 'src/config/appState';
import { redirectToUrl } from 'src/modules/navigation/actions/navigationActions';
import { color, font } from 'src/shared/styles/variables';

import { ProgramEnum, ProgramWeek, ProgramWeekEnum } from '../../models/programs';
import { getProgramWeeksDataByProgramId } from '../../selectors/programsSelectors';
import { CorePillarsItem } from './CorePillarsItem';

const CorePillarsLink = styled('div')({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
    cursor: 'pointer',
    marginBottom: '1rem',
});

const ShowAllButton = styled.p(
    {
        backgroundColor: color.linkWaterLight,
        borderRadius: 5,
        padding: '11px 14px 8px',
        color: color.steelGray,
        cursor: 'pointer',
        transition: '.3s background-color',
        lineHeight: '14px',

        ':hover': {
            backgroundColor: '#D9DFEA',
        },
    },
    font.heavy(font.size.medium),
);

const HeadingText = styled('h3')(
    {
        color: color.pickledBluewood,
        margin: 0,
        '* + &': {
            marginTop: '3rem',
        },
    },
    font.heavy(font.size.large),
);

interface CorePillarsListProps {
    vertical?: boolean;
    noMargin?: boolean;
    webExclusive?: boolean;
}

const CorePillarsListWrapper = styled('div')((props: CorePillarsListProps) => ({
    display: 'flex',
    flexFlow: props.vertical ? 'column' : 'row wrap',
    margin: props.noMargin ? '0 -0.5%' : '0 -0.5% 3rem',
}));

interface StateProps {
    weeksData: ProgramWeek[];
}

interface ActionsProps {
    redirectToUrl: typeof redirectToUrl;
}

interface Props extends StateProps, ActionsProps, WithTranslation {
    vertical?: boolean;
    noLinks?: boolean;
    noMargin?: boolean;
    webExclusive?: boolean;
}

enum CorePillarsListTestID {
    Container = 'CorePillarsListTestID.Container',
}

class CorePillarsListContainer extends React.Component<Props> {
    private handleCorePillarsOverviewClick = () =>
        this.props.redirectToUrl(`/${ProgramEnum.CP}-overview`);

    private handleCorePillarClick = (id: ProgramWeekEnum) => {
        this.props.redirectToUrl(`/programs/${ProgramEnum.CP}/${id}`);
    };

    public render(): JSX.Element {
        const { noLinks, vertical, weeksData, noMargin, webExclusive, t } = this.props;

        return (
            <div
                style={{ display: 'inline-flex', flexDirection: 'column', width: '100%' }}
                data-testid={CorePillarsListTestID.Container}
            >
                {!noLinks && (
                    <CorePillarsLink>
                        <HeadingText>{t('dashboard.corePillarsProgram.title')}</HeadingText>
                        <ShowAllButton onClick={this.handleCorePillarsOverviewClick}>
                            {t('dashboard:show_all')}
                        </ShowAllButton>
                    </CorePillarsLink>
                )}
                <CorePillarsListWrapper vertical={vertical} noMargin={noMargin}>
                    {weeksData.map(week => (
                        <CorePillarsItem
                            key={week.id}
                            weekId={week.id}
                            wide={vertical}
                            onClick={() => this.handleCorePillarClick(week.id)}
                            title={week.title}
                            iconUrl={week.iconUrl}
                            description={week.description}
                            startWeekText={this.props.t('dashboard.programs.startWeek', {
                                number: week.readableWeekNumber,
                            })}
                            webExclusive={webExclusive}
                        />
                    ))}
                </CorePillarsListWrapper>
            </div>
        );
    }
}

const mapStateToProps = (state: AppState): StateProps => ({
    weeksData: getProgramWeeksDataByProgramId(state, ProgramEnum.CP),
});

export const CorePillarsList = withTranslation()(
    connect<StateProps, ActionsProps>(mapStateToProps, {
        redirectToUrl,
    })(CorePillarsListContainer),
);
